import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";
import JsonLd from "@/components/JsonLd";
import { getPost, blogCommon, BLOG_DATE } from "@/i18n/blogPosts";
import { serviceLinks, serviceLinksTitle } from "@/i18n/seoPages";

const SITE = "https://reflectumpellicule.com";

export default function BlogPost() {
  const { slug } = useParams();
  const { lang } = useLanguage();
  const post = getPost(lang, slug);
  const c = blogCommon[lang];

  useEffect(() => {
    if (!post) return undefined;
    document.title = post.metaTitle;
    const tag = document.querySelector('meta[name="description"]');
    if (tag) tag.setAttribute("content", post.metaDesc);
    const canonical = document.querySelector('link[rel="canonical"]');
    const prev = canonical?.getAttribute("href");
    if (canonical) canonical.setAttribute("href", `${SITE}${post.path}`);
    return () => {
      if (canonical && prev) canonical.setAttribute("href", prev);
    };
  }, [post, lang]);

  if (!post) return <Navigate to="/conseils" replace />;

  const postLd = {
    "@context": "https://schema.org", "@type": "BlogPosting",
    headline: post.title,
    description: post.metaDesc,
    inLanguage: lang,
    datePublished: BLOG_DATE,
    mainEntityOfPage: `${SITE}${post.path}`,
    author: { "@type": "Organization", name: "Reflectum Pellicule", url: SITE },
    publisher: { "@type": "Organization", name: "Reflectum Pellicule", url: SITE },
  };

  return (
    <div className="animate-fade-up">
      <JsonLd id={`post-${slug}`} data={postLd} />

      <section className="py-20 sm:py-28 lg:py-36 bg-slate-50 border-b border-slate-200">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <Link to="/conseils" data-testid="post-back" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            {c.backToBlog}
          </Link>
          <div className="mt-8 text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-600">
            {c.eyebrow}, {c.published} {BLOG_DATE}
          </div>
          <h1 data-testid="post-title" className="font-heading text-[2.25rem] sm:text-5xl font-normal tracking-tight leading-[1.15] text-slate-900 mt-4">
            {post.title}
          </h1>
        </div>
      </section>

      <article className="py-20 sm:py-28 lg:py-32 bg-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-14">
          {post.sections.map((s, i) => (
            <section key={s.title} data-testid={`post-section-${i}`}>
              <h2 className="font-heading text-2xl lg:text-3xl font-normal tracking-tight leading-[1.2] text-slate-900">
                {s.title}
              </h2>
              <p className="mt-5 text-lg text-slate-600 leading-relaxed">{s.text}</p>
            </section>
          ))}
        </div>
      </article>

      <section className="py-20 sm:py-28 lg:py-36 bg-slate-950 text-white">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl lg:text-5xl font-normal tracking-tight leading-[1.2]">{c.ctaTitle}</h2>
          <p className="mt-6 text-lg text-slate-300 leading-relaxed">{c.ctaText}</p>
          <Link to="/contact" data-testid="post-cta" className="block sm:inline-block">
            <Button className="w-full sm:w-auto mt-10 rounded-full bg-blue-600 hover:bg-blue-700 text-white h-auto min-h-14 px-6 py-3.5 text-base font-medium shadow-lg shadow-black/10 whitespace-normal text-center leading-snug">
              {c.ctaButton}
              <ArrowRight className="ml-2 w-4 h-4 flex-shrink-0" />
            </Button>
          </Link>
          <div className="mt-16 pt-10 border-t border-white/10">
            <div className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-blue-400 mb-6">{serviceLinksTitle[lang]}</div>
            <div className="flex flex-wrap justify-center gap-3">
              {serviceLinks[lang].map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  data-testid={`post-service-${l.to.slice(1)}`}
                  className="px-4 py-2 rounded-full border border-white/15 bg-white/5 text-sm text-slate-200 hover:bg-white/10 transition-colors"
                >
                  {l.label}
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
